<template>
  <div
    v-if="isOpen"
    class="userscript-editor-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="userscript-editor-title"
    @click.self="requestClose"
  >
    <section class="userscript-editor-modal">
      <header class="userscript-editor-header">
        <div>
          <span class="userscript-editor-eyebrow">{{ isCodeMode ? 'CODE' : 'USER SCRIPT' }}</span>
          <h2 id="userscript-editor-title">{{ label('title') }}</h2>
          <p>{{ artifactName || label('defaultName') }}</p>
        </div>
        <button
          type="button"
          class="userscript-editor-close"
          :aria-label="label('close')"
          :title="label('close')"
          :disabled="saving"
          @click="requestClose"
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div class="userscript-editor-body">
        <section class="userscript-code-panel">
          <div class="userscript-panel-heading">
            <div>
              <strong>{{ label('sourceTitle') }}</strong>
              <span>{{ source.length.toLocaleString() }} chars</span>
            </div>
            <span class="userscript-dirty-state" :class="{ dirty: isDirty }">
              {{ isDirty ? label('unsaved') : label('saved') }}
            </span>
          </div>
          <textarea
            ref="sourceInput"
            v-model="source"
            class="userscript-source-input"
            spellcheck="false"
            :disabled="modifying || saving"
            :aria-label="label('sourceTitle')"
          ></textarea>
        </section>

        <aside class="userscript-ai-panel">
          <div class="userscript-ai-heading">
            <span class="userscript-ai-mark" aria-hidden="true">✦</span>
            <div>
              <strong>{{ label('aiTitle') }}</strong>
              <p>{{ label('aiDescription') }}</p>
            </div>
          </div>
          <form class="userscript-prompt-form" @submit.prevent="modifyWithAi">
            <label for="userscript-edit-prompt">{{ label('promptLabel') }}</label>
            <textarea
              id="userscript-edit-prompt"
              v-model.trim="instruction"
              rows="5"
              maxlength="4000"
              :disabled="modifying || saving"
              :placeholder="label('promptPlaceholder')"
            ></textarea>
            <div class="userscript-prompt-actions">
              <span>{{ instruction.length }}/4000</span>
              <button
                v-if="!modifying"
                type="submit"
                class="userscript-primary-button"
                :disabled="!instruction || saving"
              >
                {{ label('modify') }}
              </button>
              <button
                v-else
                type="button"
                class="userscript-secondary-button"
                @click="stopModification"
              >
                {{ label('stop') }}
              </button>
            </div>
          </form>
          <div v-if="modificationSteps.length" ref="modificationSteps" class="userscript-agent-steps" aria-live="polite">
            <div
              v-for="(step, index) in modificationSteps"
              :key="index"
              class="userscript-agent-step"
              :class="{ failed: step.failed, pending: step.pending }"
            >
              <span class="userscript-agent-step-index">{{ index + 1 }}</span>
              <div>
                <strong>{{ step.title }}</strong>
                <p>{{ step.message }}</p>
                <details v-if="step.detail">
                  <summary>{{ label('viewStep') }}</summary>
                  <pre>{{ step.detail }}</pre>
                </details>
              </div>
            </div>
          </div>
          <div v-if="statusMessage" class="userscript-status" :class="{ error: statusError }">
            {{ statusMessage }}
          </div>
          <p class="userscript-safety-note">{{ label('safetyNote') }}</p>
        </aside>
      </div>

      <footer class="userscript-editor-footer">
        <span v-if="saving" class="userscript-saving-state">{{ label('publishing') }}</span>
        <span v-else class="userscript-editor-hint">{{ label('footerHint') }}</span>
        <div class="userscript-footer-actions">
          <button type="button" class="userscript-secondary-button" :disabled="saving" @click="requestClose">
            {{ label('cancel') }}
          </button>
          <button
            type="button"
            class="userscript-primary-button"
            :disabled="saving || modifying || !isDirty || !source.trim()"
            @click="saveAndClose"
          >
            {{ saving ? label('publishing') : label('save') }}
          </button>
        </div>
      </footer>
    </section>
  </div>
</template>

<script>
import { showConfirm } from '@/utils/dialog';
import { modifyUserscriptStep } from '@/api/workbench';

const MAX_SOURCE_LENGTH = 512 * 1024;

// Code blocks keep their exact whitespace; only the trailing newline added by markdown fences is dropped.
function normalizeCodeSource(value) {
  return String(value || '').replace(/\r?\n$/, '');
}

function normalizeSource(value, original = '') {
  let source = String(value || '').trim();
  const fenced = source.match(/^```(?:javascript|js|userscript)?\s*([\s\S]*?)\s*```$/i);
  if (fenced) source = fenced[1].trim();

  if (!source) return '';
  if (!source.includes('==UserScript==') && original.includes('==UserScript==')) {
    const header = original.match(/^\s*\/\/\s*==UserScript==[\s\S]*?\/\/\s*==\/UserScript==\s*/i);
    if (header) source = `${header[0].trim()}\n\n${source}`;
  }
  return source;
}

function extractUploadUrl(rawResponse) {
  let result;
  try {
    result = typeof rawResponse === 'string' ? JSON.parse(rawResponse) : rawResponse;
  } catch (error) {
    throw new Error('脚本发布接口返回了无效数据');
  }
  if (!result || !result.fileurl) {
    throw new Error((result && (result.msg || result.message)) || '脚本发布失败');
  }
  return result.fileurl;
}

export default {
  name: 'UserscriptEditorModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    artifact: {
      type: Object,
      default: null
    },
    // 'userscript' publishes the script; 'code' edits a chat code block in place.
    mode: {
      type: String,
      default: 'userscript'
    }
  },
  data() {
    return {
      source: '',
      originalSource: '',
      originalUrl: '',
      instruction: '',
      modifying: false,
      saving: false,
      statusMessage: '',
      statusError: false,
      modificationAbortController: null,
      modificationRunId: 0,
      modificationSteps: []
    };
  },
  computed: {
    isDirty() {
      return this.source !== this.originalSource;
    },
    isCodeMode() {
      return this.mode === 'code';
    },
    artifactName() {
      if (this.isCodeMode) return this.artifact && this.artifact.language;
      return this.artifact && (this.artifact.name || this.artifact.url || this.artifact.publishedUrl);
    }
  },
  watch: {
    isOpen(value) {
      if (value) this.resetEditor();
      else this.stopModification();
    },
    artifact: {
      deep: true,
      handler() {
        if (this.isOpen) this.resetEditor();
      }
    }
  },
  mounted() {
    if (this.isOpen) this.resetEditor();
  },
  beforeDestroy() {
    this.stopModification();
  },
  methods: {
    label(key, params) {
      const codeKey = `codeEditor.${key}`;
      if (this.isCodeMode && this.$te(codeKey)) return this.$t(codeKey, params);
      return this.$t(`userscriptEditor.${key}`, params);
    },
    normalize(value, original) {
      return this.isCodeMode ? normalizeCodeSource(value) : normalizeSource(value, original);
    },
    resetEditor() {
      const nextSource = this.normalize(this.artifact && this.artifact.source);
      this.source = nextSource;
      this.originalSource = nextSource;
      this.originalUrl = String((this.artifact && (this.artifact.url || this.artifact.publishedUrl)) || '');
      this.instruction = '';
      this.modifying = false;
      this.saving = false;
      this.statusMessage = '';
      this.statusError = false;
      this.modificationAbortController = null;
      this.modificationRunId += 1;
      this.modificationSteps = [];
      this.$nextTick(() => {
        if (this.$refs.sourceInput) this.$refs.sourceInput.focus();
      });
    },
    setStatus(message, isError = false) {
      this.statusMessage = message;
      this.statusError = isError;
    },
    validateSource(source) {
      const value = this.normalize(source, this.originalSource);
      if (!value.trim()) throw new Error(this.label('emptySource'));
      if (value.length > MAX_SOURCE_LENGTH) throw new Error(this.label('sourceTooLarge'));
      // Chat code blocks can be any language, so only userscripts get a JS compile check.
      if (this.isCodeMode) return value;
      try {
        // Compilation catches accidental truncated output without executing the script.
        // UserScript metadata is comments, so it is safe to pass through Function here.
        // eslint-disable-next-line no-new-func
        new Function(value);
      } catch (error) {
        throw new Error(`${this.label('syntaxError')}: ${error.message}`);
      }
      return value;
    },
    async modifyWithAi() {
      if (!this.instruction || this.modifying || this.saving) return;
      const runId = ++this.modificationRunId;
      const instruction = this.instruction;
      const history = [];
      const controller = new AbortController();
      let appliedChanges = 0;
      let pendingStepIndex = -1;
      this.modifying = true;
      this.modificationAbortController = controller;
      this.modificationSteps.push({ title: this.label('requestStep'), message: instruction, detail: '' });
      this.scrollModificationStepsToBottom();

      try {
        for (let round = 0; round < 16; round += 1) {
          pendingStepIndex = this.modificationSteps.length;
          this.modificationSteps.push({
            title: this.label('waitingStep'),
            message: this.label('waitingStepMessage', { count: round }),
            detail: '',
            pending: true
          });
          this.scrollModificationStepsToBottom();

          const command = await modifyUserscriptStep({
            source: this.source,
            instruction,
            history,
            target: this.isCodeMode ? 'code' : 'userscript',
            language: this.isCodeMode ? String((this.artifact && this.artifact.language) || '') : ''
          }, controller.signal);
          if (runId !== this.modificationRunId) return;

          const execution = this.executeModificationTool(command);
          if (execution.applied) appliedChanges += 1;
          this.$set(this.modificationSteps, pendingStepIndex, this.presentModificationStep(command, execution.result));
          this.scrollModificationStepsToBottom();
          pendingStepIndex = -1;

          if (command.tool === 'finish_edit') {
            if (!appliedChanges) throw new Error(this.label('noChanges'));
            this.instruction = '';
            this.setStatus(this.label('modifySuccess'));
            this.modificationSteps.push({
              title: this.label('editComplete'),
              message: execution.result.summary || this.label('modifySuccess'),
              detail: ''
            });
            this.scrollModificationStepsToBottom();
            return;
          }
          history.push({ tool: command.tool, arguments: command.arguments || {}, result: execution.result });
        }
        throw new Error(this.label('tooManySteps'));
      } catch (error) {
        if (runId !== this.modificationRunId) return;
        const failedStep = {
          title: this.label('editStopped'),
          message: error && error.name === 'AbortError'
            ? this.label('modifyStopped')
            : (error.message || this.label('modifyFailed')),
          detail: '',
          failed: true
        };
        if (pendingStepIndex >= 0) this.$set(this.modificationSteps, pendingStepIndex, failedStep);
        else this.modificationSteps.push(failedStep);
        this.setStatus(failedStep.message, true);
        this.scrollModificationStepsToBottom();
      } finally {
        if (runId === this.modificationRunId) {
          this.modifying = false;
          this.modificationAbortController = null;
        }
      }
    },
    scrollModificationStepsToBottom(focusInput = false) {
      this.$nextTick(() => {
        const steps = this.$refs.modificationSteps;
        if (steps) steps.scrollTop = steps.scrollHeight;
        if (focusInput && this.$refs.sourceInput) this.$refs.sourceInput.focus();
      });
    },
    executeModificationTool(command) {
      const tool = command && command.tool;
      const args = (command && command.arguments) || {};
      const toolSource = this.source;
      if (tool === 'search_source') {
        const query = String(args.query || '');
        if (!query) return { result: { ok: false, error: '搜索关键字不能为空' } };
        const sourceLower = toolSource.toLocaleLowerCase();
        const queryLower = query.toLocaleLowerCase();
        const matches = [];
        let index = 0;
        let line = 1;
        let cursor = 0;
        let total = 0;
        while ((index = sourceLower.indexOf(queryLower, index)) !== -1) {
          line += (toolSource.slice(cursor, index).match(/\n/g) || []).length;
          total += 1;
          if (matches.length < 20) {
            matches.push({ line, snippet: toolSource.slice(Math.max(0, index - 100), index + query.length + 140).replace(/\s+/g, ' ').slice(0, 360) });
          }
          cursor = index;
          index += Math.max(query.length, 1);
        }
        return { result: { ok: true, query, total_matches: total, matches, truncated: total > matches.length } };
      }
      if (tool === 'read_source') {
        const lines = toolSource.split(/\r?\n/);
        const start = Math.max(1, Number(args.start_line) || 1);
        const end = Math.min(lines.length, Math.max(start, Math.min(Number(args.end_line) || start, start + 159)));
        const selectedLines = [];
        let budget = 28000;
        for (let index = start - 1; index < end && budget > 0; index += 1) {
          const original = lines[index] || '';
          const text = original.slice(0, Math.min(original.length, budget, 4000));
          selectedLines.push({ line: index + 1, text, truncated: text.length < original.length });
          budget -= text.length + 40;
        }
        return { result: { ok: true, start_line: start, end_line: selectedLines.length ? selectedLines[selectedLines.length - 1].line : start, total_lines: lines.length, lines: selectedLines, truncated: selectedLines.length < end - start + 1 } };
      }
      if (tool === 'replace_source') {
        const oldText = String(args.old_text || '');
        const newText = String(args.new_text || '');
        let matchText = oldText;
        let replacementText = newText;
        let occurrences = oldText ? this.source.split(matchText).length - 1 : 0;
        if (!occurrences && oldText.includes('\n')) {
          const crlfText = oldText.replace(/\n/g, '\r\n');
          occurrences = this.source.split(crlfText).length - 1;
          if (occurrences) {
            matchText = crlfText;
            replacementText = newText.replace(/\n/g, '\r\n');
          }
        }
        if (occurrences !== 1) {
          return { result: { ok: false, applied: false, occurrences, error: occurrences === 0 ? '精确旧文本未找到，请重新读取源码' : '精确旧文本出现多次，请扩大上下文后重试' } };
        }
        this.source = this.source.replace(matchText, replacementText);
        return { applied: true, result: { ok: true, applied: true, removed_chars: oldText.length, inserted_chars: newText.length } };
      }
      if (tool === 'finish_edit') return { result: { ok: true, summary: String(args.summary || '修改完成') } };
      throw new Error('AI 返回了不支持的源码工具');
    },
    presentModificationStep(command, result) {
      const args = command.arguments || {};
      const reason = args.reason ? `，${args.reason}` : '';
      let title = this.label('toolStep');
      let message = reason.replace(/^，/, '');
      if (command.tool === 'search_source') {
        title = this.label('searchStep');
        message = `查找“${args.query}”，找到 ${result.total_matches || 0} 处${reason}`;
      } else if (command.tool === 'read_source') {
        title = this.label('readStep');
        message = `读取第 ${result.start_line}-${result.end_line} 行${reason}`;
      } else if (command.tool === 'replace_source') {
        title = result.applied ? this.label('replaceStep') : this.label('replaceFailed');
        message = result.applied ? (args.reason || this.label('replaceStep')) : (result.error || this.label('replaceFailed'));
      } else if (command.tool === 'finish_edit') {
        title = this.label('finishStep');
        message = result.summary || this.label('modifySuccess');
      }
      return {
        title,
        message,
        failed: result.ok === false,
        detail: JSON.stringify({ command: command.tool, arguments: args, result }, null, 2)
      };
    },
    stopModification() {
      const wasModifying = this.modifying;
      if (this.modificationAbortController) this.modificationAbortController.abort();
      this.modificationRunId += 1;
      this.modificationAbortController = null;
      this.modifying = false;
      if (wasModifying) {
        this.modificationSteps.push({
          title: this.label('editStopped'),
          message: this.label('modifyStopped'),
          detail: ''
        });
        this.setStatus(this.label('modifyStopped'));
        this.scrollModificationStepsToBottom(true);
      }
    },
    async requestClose() {
      if (this.saving) return;
      if (this.isDirty) {
        const confirmed = await showConfirm(this.label('discardConfirm'));
        if (!confirmed) return;
      }
      this.$emit('close');
    },
    async publishSource(source) {
      const blob = new Blob([source], { type: 'application/javascript;charset=utf-8' });
      const formData = new FormData();
      formData.append('file', blob, 'generated_script.user.js');
      const response = await fetch('https://picmain.rainsee.cn/api.php', {
        method: 'POST',
        body: formData
      });
      const rawResponse = await response.text();
      if (!response.ok) throw new Error(`脚本发布失败：${response.status}`);
      return extractUploadUrl(rawResponse);
    },
    async saveAndClose() {
      if (this.saving || this.modifying) return;
      this.saving = true;
      this.setStatus('');
      try {
        const nextSource = this.validateSource(this.source);
        if (this.isCodeMode) {
          this.$emit('saved', { source: nextSource, updatedAt: Date.now() });
          return;
        }
        const nextUrl = await this.publishSource(nextSource);
        this.$emit('saved', {
          source: nextSource,
          url: nextUrl,
          previousUrl: this.originalUrl,
          version: Number(this.artifact && this.artifact.version || 1) + 1,
          updatedAt: Date.now()
        });
      } catch (error) {
        this.setStatus(error.message || this.label('publishFailed'), true);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.userscript-editor-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.54);
}

.userscript-editor-modal {
  display: flex;
  flex-direction: column;
  width: min(1180px, 100%);
  height: min(820px, calc(100vh - 48px));
  min-height: 520px;
  overflow: hidden;
  border: 1px solid #d9e0e7;
  border-radius: 12px;
  background: #f7f9fb;
  color: #17202b;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.32);
}

.userscript-editor-header,
.userscript-editor-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex: 0 0 auto;
  padding: 18px 22px;
  background: #fff;
}

.userscript-editor-header {
  border-bottom: 1px solid #e2e7ec;
}

.userscript-editor-eyebrow {
  color: #0f766e;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
}

.userscript-editor-header h2 {
  margin: 4px 0 2px;
  font-size: 20px;
  line-height: 1.25;
}

.userscript-editor-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.userscript-editor-close {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-size: 27px;
  line-height: 1;
}

.userscript-editor-close:hover {
  background: #eef2f5;
  color: #17202b;
}

.userscript-editor-body {
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(300px, 0.75fr);
  gap: 14px;
  flex: 1 1 auto;
  min-height: 0;
  padding: 14px;
}

.userscript-code-panel,
.userscript-ai-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid #dbe2e8;
  border-radius: 8px;
  background: #fff;
}

.userscript-panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 50px;
  padding: 0 14px;
  border-bottom: 1px solid #e4e9ee;
}

.userscript-panel-heading > div {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.userscript-panel-heading span,
.userscript-dirty-state {
  color: #8491a1;
  font-size: 12px;
}

.userscript-dirty-state.dirty {
  color: #b45309;
}

.userscript-source-input {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  resize: none;
  padding: 16px;
  border: 0;
  outline: 0;
  background: #17202b;
  color: #e5edf5;
  font: 13px/1.65 Consolas, 'SFMono-Regular', Menlo, monospace;
  tab-size: 2;
}

.userscript-ai-panel {
  padding: 18px;
}

.userscript-ai-heading {
  display: flex;
  gap: 11px;
  align-items: flex-start;
  padding-bottom: 16px;
  border-bottom: 1px solid #e4e9ee;
}

.userscript-ai-mark {
  display: grid;
  place-items: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: #dff5f1;
  color: #0f766e;
}

.userscript-ai-heading strong {
  display: block;
  margin: 3px 0 5px;
}

.userscript-ai-heading p,
.userscript-safety-note {
  margin: 0;
  color: #718096;
  font-size: 12px;
  line-height: 1.6;
}

.userscript-prompt-form {
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 18px 0;
}

.userscript-prompt-form label {
  color: #344256;
  font-size: 13px;
  font-weight: 650;
}

.userscript-prompt-form textarea {
  width: 100%;
  min-height: 112px;
  resize: vertical;
  box-sizing: border-box;
  padding: 11px 12px;
  border: 1px solid #ced8e2;
  border-radius: 7px;
  outline: 0;
  color: #17202b;
  font-size: 14px;
  line-height: 1.55;
  font-family: inherit;
}

.userscript-prompt-form textarea:focus {
  border-color: #0f766e;
  box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
}

.userscript-prompt-actions,
.userscript-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
}

.userscript-prompt-actions > span {
  margin-right: auto;
  color: #94a3b8;
  font-size: 11px;
}

.userscript-agent-steps {
  max-height: 245px;
  overflow-y: auto;
  margin-bottom: 14px;
  padding-right: 3px;
}

.userscript-agent-step {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 9px;
  padding: 10px 0;
  border-bottom: 1px solid #edf0f2;
}

.userscript-agent-step:last-child {
  border-bottom: 0;
}

.userscript-agent-step-index {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e8eef1;
  color: #536273;
  font-size: 11px;
  font-weight: 700;
}

.userscript-agent-step.pending .userscript-agent-step-index {
  background: #e2f3f0;
  color: #0f766e;
}

.userscript-agent-step.failed .userscript-agent-step-index {
  background: #ffe4e6;
  color: #be123c;
}

.userscript-agent-step strong {
  display: block;
  margin: 2px 0 4px;
  color: #344256;
  font-size: 12px;
}

.userscript-agent-step p {
  margin: 0;
  color: #718096;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-word;
}

.userscript-agent-step details {
  margin-top: 6px;
  color: #718096;
  font-size: 11px;
}

.userscript-agent-step summary {
  cursor: pointer;
}

.userscript-agent-step pre {
  max-height: 150px;
  overflow: auto;
  margin: 6px 0 0;
  padding: 8px;
  border-radius: 5px;
  background: #f4f6f8;
  white-space: pre-wrap;
  word-break: break-word;
}

.userscript-primary-button,
.userscript-secondary-button {
  min-height: 38px;
  padding: 0 14px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
}

.userscript-primary-button {
  border: 1px solid #0f766e;
  background: #0f766e;
  color: #fff;
}

.userscript-primary-button:hover:not(:disabled) {
  background: #0b625c;
}

.userscript-secondary-button {
  border: 1px solid #ced8e2;
  background: #fff;
  color: #465568;
}

.userscript-secondary-button:hover:not(:disabled) {
  background: #f2f5f7;
}

.userscript-primary-button:disabled,
.userscript-secondary-button:disabled,
.userscript-editor-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.userscript-status {
  padding: 10px 11px;
  border-radius: 6px;
  background: #eef8f6;
  color: #0f766e;
  font-size: 12px;
  line-height: 1.5;
}

.userscript-status.error {
  background: #fff1f2;
  color: #be123c;
}

.userscript-safety-note {
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid #e4e9ee;
}

.userscript-editor-footer {
  border-top: 1px solid #e2e7ec;
}

.userscript-editor-hint,
.userscript-saving-state {
  color: #718096;
  font-size: 12px;
}

.userscript-saving-state {
  color: #0f766e;
}

@media (max-width: 760px) {
  .userscript-editor-overlay {
    align-items: stretch;
    padding: 0;
  }

  .userscript-editor-modal {
    width: 100%;
    height: 100%;
    min-height: 0;
    border: 0;
    border-radius: 0;
  }

  .userscript-editor-header,
  .userscript-editor-footer {
    padding: 14px 16px;
  }

  .userscript-editor-body {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding: 10px;
  }

  .userscript-code-panel {
    min-height: 420px;
    order: 2;
  }

  .userscript-ai-panel {
    flex: 0 0 auto;
    order: 1;
  }

  .userscript-editor-footer {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .userscript-editor-hint,
  .userscript-saving-state {
    display: none;
  }

  .userscript-footer-actions {
    justify-content: stretch;
  }

  .userscript-footer-actions button {
    flex: 1;
  }
}
</style>
